import { Box, Button, Center, Flex, FormControl, FormErrorMessage, FormLabel, Heading, HStack, Image, Input, PinInput, PinInputField, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import ModalLoading from '../../components/Loader/ModalLoader.jsx';
import { trackRegistWaiver } from '../../helper/utils/eventTrackGA.js';
import { urlNavigate } from '../../helper/utils/navigation.js';
import getStaticAsset from '../../helper/utils/utilsStaticAsset';
import { doSuccess } from '../../redux/responseHandling/responseHandling.actions.js';
import { doCheckEmail, doLogin, doSendOTP, doSetPassword, doVerifyOTP } from '../../redux/waiver/waiver.actions.js';

const EmailSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
});

const PasswordSchema = Yup.object().shape({
    password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm password is required'),
});

function FormEmailPassword() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const page = JSON.parse(localStorage.getItem('page') || '{}');
    const currentEmail = String(localStorage.getItem('email') || '');

    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Password
    const [email, setEmail] = useState('');
    const [pin, setPin] = useState('');
    const [seconds, setSeconds] = useState(60);
    const [params, setParams] = useSearchParams();

    const { loadingCheckPhone = false, loadingVerifyOTP = false, loadingRegister = false, loadingSendOTP = false } = useSelector((s) => s?.waiver);

    // Email
    const emailFormik = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema: EmailSchema,
        onSubmit: (values) => {
            dispatch(
                doSendOTP(values, navigate, () => {
                    setEmail(values.email);
                    localStorage.setItem('emailOTP', values.email);

                    setStep(2);
                    resetTimer();
                }),
            );
        },
    });

    // Password
    const passwordFormik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: '',
            otp: '',
        },
        validationSchema: PasswordSchema,
        onSubmit: (values) => {
            // Save email and password to localStorage
            localStorage.setItem('email', email);
            localStorage.setItem('password', values.password);

            dispatch(
                doSetPassword({ email, password: values.password, otp: values.otp || pin }, navigate, () => {
                    trackRegistWaiver({
                        label: 'user sign password for new account',
                    });

                    dispatch(
                        doLogin({ email, password: values.password, brand: page['brandName'] || '' }, navigate, () => {
                            dispatch(
                                doCheckEmail({ email, password: values.password }, navigate, (data) => {
                                    const callbackUrl = (params.get('callback') || '').toString();

                                    dispatch(doSuccess(`Welcome, ${data['fullname'] || ''}!`, '', 'Verify'));

                                    setParams({
                                        date: params.get('date') || '',
                                        productId: params.get('productId') || '',
                                        isShowDetail: (params.get('productId') || '') !== '',
                                        type: params.get('type') || '',
                                        callback: callbackUrl,
                                    });

                                    if (callbackUrl && callbackUrl != '') {
                                        navigate(callbackUrl);
                                    } else {
                                        navigate(urlNavigate('online-sales', 'home'));
                                    }
                                }),
                            );
                        }),
                    );
                }),
            );
        },
    });

    const handleSendOtp = (email = '') => {
        dispatch(
            doSendOTP({ email }, navigate, () => {
                setEmail(email);
                localStorage.setItem('emailOTP', email);

                setStep(2);
                resetTimer();
            }),
        );
    };

    // OTP
    useEffect(() => {
        if (step === 2 && seconds > 0) {
            const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [seconds, step]);

    useEffect(() => {
        if (currentEmail) {
            setEmail(currentEmail);
            emailFormik.setFieldValue('email', currentEmail);

            handleSendOtp(currentEmail);
        }
    }, [currentEmail]);

    const formatTime = (secs) => {
        const minutes = Math.floor(secs / 60);
        const remainingSeconds = secs % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const resetTimer = () => setSeconds(60);

    const handlePinChange = (value) => {
        setPin(value);
        passwordFormik.setFieldValue('otp', value);
    };

    const handleVerifyOTP = () => {
        if (pin.length === 6) {
            dispatch(
                doVerifyOTP({ email: email || currentEmail, otp: pin }, navigate, () => {
                    setStep(3);
                }),
            );
        }
    };

    const handleResendOTP = () => {
        resetTimer();
        setPin('');
        handleSendOtp(currentEmail || email);
    };

    const isLoading = loadingCheckPhone || loadingVerifyOTP || loadingRegister || loadingSendOTP;

    return (
        <>
            <ModalLoading loading={isLoading} />
            <Box
                minH='100vh'
                w='full'
                bgImage={getStaticAsset(page['home']['image'])}
                bgSize='cover'
                bgPosition='center'
                bgRepeat='no-repeat'
                position='relative'
                _before={{
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 0,
                }}>
                <Center minH='100vh' position='relative' zIndex={1} p={4}>
                    <Box maxW='500px' w='full' bg={useColorModeValue('white', 'gray.800')} rounded='2xl' boxShadow='2xl' p={{ base: 6, md: 8 }} backdropFilter='blur(10px)'>
                        {/* Logo */}
                        <Flex w='full' alignItems='center' justifyContent='center' gap={5} mb={2}>
                            <Image src={getStaticAsset('dream_us_logo.webp')} alt='Dream Us Logo' w='100px' />
                            <Text fontSize='xl' color='gray.500'>
                                X
                            </Text>
                            <Image src={getStaticAsset(page['logo'] || '')} alt='Dream Us Logo' w='100px' /> {/* Logo */}
                        </Flex>

                        {/* 1 - Email Input */}
                        {step === 1 && (
                            <VStack spacing={6} align='stretch'>
                                <Box textAlign='center'>
                                    <Heading fontSize={{ base: '2xl', md: '3xl' }} mb={2}>
                                        Welcome
                                    </Heading>
                                    <Text color='gray.600' fontSize='md'>
                                        Enter your email to get started
                                    </Text>
                                </Box>

                                <form onSubmit={emailFormik.handleSubmit}>
                                    <VStack spacing={2} align='stretch'>
                                        <FormControl isInvalid={!!emailFormik.errors.email && emailFormik.touched.email}>
                                            <FormLabel htmlFor='email'>Email Address</FormLabel>
                                            <Input id='email' name='email' type='email' size='lg' placeholder='Enter your email' value={emailFormik.values.email} onChange={emailFormik.handleChange} onBlur={emailFormik.handleBlur} />
                                            <FormErrorMessage>{emailFormik.errors.email}</FormErrorMessage>
                                        </FormControl>

                                        <Button isLoading={isLoading} type='submit' fontSize='md' colorScheme='primary' size='lg' w='full' mt={4}>
                                            Get Your OTP
                                        </Button>
                                    </VStack>
                                </form>
                            </VStack>
                        )}

                        {/* 2 - OTP Verification */}
                        {step === 2 && (
                            <VStack spacing={6} align='stretch'>
                                <Box textAlign='center'>
                                    <Heading fontSize={{ base: '2xl', md: '3xl' }} mb={2}>
                                        Verify OTP
                                    </Heading>
                                    <Text color='gray.600' fontSize='sm'>
                                        We&apos;ve sent a verification code to
                                    </Text>
                                    <Text color='primary.500' fontWeight={600} fontSize='sm' mt={1}>
                                        {email}
                                    </Text>
                                </Box>

                                <VStack spacing={4} align='stretch'>
                                    <FormControl>
                                        <FormLabel textAlign='center'>Enter 6-digit code</FormLabel>
                                        <Center>
                                            <HStack spacing={2}>
                                                <PinInput otp size='lg' value={pin} onChange={handlePinChange} onComplete={handlePinChange}>
                                                    <PinInputField />
                                                    <PinInputField />
                                                    <PinInputField />
                                                    <PinInputField />
                                                    <PinInputField />
                                                    <PinInputField />
                                                </PinInput>
                                            </HStack>
                                        </Center>
                                    </FormControl>

                                    <Button colorScheme='gray' variant='ghost' size='sm' isDisabled={seconds > 0} onClick={handleResendOTP}>
                                        {seconds > 0 ? `Resend OTP in ${formatTime(seconds)}` : 'Resend OTP'}
                                    </Button>

                                    <Button isLoading={isLoading} colorScheme='primary' size='lg' fontSize='md' w='full' isDisabled={pin.length !== 6} onClick={handleVerifyOTP} mt={2}>
                                        Verify & Continue
                                    </Button>

                                    <Button
                                        variant='ghost'
                                        size='md'
                                        isLoading={isLoading}
                                        onClick={() => {
                                            setStep(1);
                                            setPin('');
                                        }}>
                                        Change Email
                                    </Button>
                                </VStack>
                            </VStack>
                        )}

                        {/* 3 - Password Creation */}
                        {step === 3 && (
                            <VStack spacing={6} align='stretch'>
                                <Box textAlign='center'>
                                    <Heading fontSize={{ base: '2xl', md: '3xl' }} mb={2}>
                                        Create Password
                                    </Heading>
                                    <Text color='gray.600' fontSize='sm'>
                                        Set up a secure password for your account
                                    </Text>
                                </Box>

                                <form onSubmit={passwordFormik.handleSubmit}>
                                    <VStack spacing={4} align='stretch'>
                                        <FormControl isInvalid={!!passwordFormik.errors.password && passwordFormik.touched.password}>
                                            <FormLabel htmlFor='password'>Password</FormLabel>
                                            <Input id='password' name='password' type='password' size='lg' placeholder='Enter password (min. 8 characters)' value={passwordFormik.values.password} onChange={passwordFormik.handleChange} onBlur={passwordFormik.handleBlur} />
                                            <FormErrorMessage>{passwordFormik.errors.password}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl isInvalid={!!passwordFormik.errors.confirmPassword && passwordFormik.touched.confirmPassword}>
                                            <FormLabel htmlFor='confirmPassword'>Confirm Password</FormLabel>
                                            <Input id='confirmPassword' name='confirmPassword' type='password' size='lg' placeholder='Re-enter your password' value={passwordFormik.values.confirmPassword} onChange={passwordFormik.handleChange} onBlur={passwordFormik.handleBlur} />
                                            <FormErrorMessage>{passwordFormik.errors.confirmPassword}</FormErrorMessage>
                                        </FormControl>

                                        <Box bg='gray.50' p={3} rounded='md' fontSize='xs' color='gray.600'>
                                            <Text fontWeight={600} mb={1}>
                                                Password requirements:
                                            </Text>
                                            <Text>• At least 8 characters long</Text>
                                        </Box>

                                        <Button type='submit' colorScheme='primary' size='lg' w='full' mt={4}>
                                            Create Account
                                        </Button>
                                    </VStack>
                                </form>
                            </VStack>
                        )}

                        {/* Progress Bar */}
                        <HStack justify='center' mt={8} spacing={2}>
                            <Box w={8} h={1} rounded='full' bg={step >= 1 ? 'primary.500' : 'gray.300'} transition='all 0.3s' />
                            <Box w={8} h={1} rounded='full' bg={step >= 2 ? 'primary.500' : 'gray.300'} transition='all 0.3s' />
                            <Box w={8} h={1} rounded='full' bg={step >= 3 ? 'primary.500' : 'gray.300'} transition='all 0.3s' />
                        </HStack>
                    </Box>
                </Center>
            </Box>
        </>
    );
}

export default FormEmailPassword;
